<template>
    <form-wrapper @submit="handleFormSubmission()">
        <div>
            <form-row>
                <form-cell>
                    <text-field
                        ref="registrationEmailInput"
                        :class="invalidInputEmailClass"
                        :label="$t('forms.auth.register.fields.email.label')"
                        :placeholder="$t('forms.auth.register.fields.email.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="email"
                        type="email"
                        @blur="checkEmailOnBlur"
                        @change="emailChange($event)"
                    >
                        <template #hint>
                            <p v-html="$t('forms.auth.register.fields.email.hint')"></p>
                        </template>

                        <template #error="{ error }">
                            <template v-if="error.code === 'entity.duplicate'">
                                {{ $t('forms.auth.register.errors.email.duplicate.message') }}
                                <router-link :to="{ name: 'auth.login' }">
                                    {{ $t('forms.auth.register.errors.email.duplicate.link') }}
                                </router-link>
                            </template>
                            <template v-else>
                                {{ error }}
                            </template>
                        </template>
                    </text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <text-field
                        ref="registrationPasswordInput"
                        :class="invalidInputPasswordClass"
                        :label="$t('forms.auth.register.fields.password.label')"
                        :placeholder="$t('forms.auth.register.fields.password.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="password"
                        :type="passwordInputFieldType"
                        :maxLength="40"
                        @blur="checkPasswordOnBlur"
                        @change="checkOnChange($event)"
                    >
                        <template #icon>
                            <common-toggle-visibility-button
                                @visibility-toggle-switch="showPassword"
                            ></common-toggle-visibility-button>
                        </template>
                    </text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <select-field
                        :label="'Type of practice'"
                        :placeholder="$t('forms.auth.register.fields.role.placeholder')"
                        :options="practiceTypes"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="practiceType"
                        :error="userRoleError"
                        @change="roleChanged($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <checkbox-field
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="agree"
                        @change="change($event)"
                    >
                        <template #label>
                            <span v-html="$t('forms.auth.register.fields.agree.label')"></span>
                        </template>
                    </checkbox-field>
                </form-cell>
            </form-row>
        </div>

        <template #footer>
            <common-buttons>
                <common-button
                    :isDisabled="!data.agree || isEmailInvalid"
                    :isProcessing="isSending"
                    :label="$t('forms.auth.register.microcopy.signup')"
                    type="primary"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import UsersAPI from 'api/UsersAPI'
    import AuthAPI from 'api/AuthAPI'
    import PasswordToggleVisibilityMixin from 'mixins/PasswordToggleVisibilityMixin'
    import FormMixin from 'mixins/FormMixin'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'

    export default {
        mixins: [FormMixin, ScreenSizeDetectionMixin, PasswordToggleVisibilityMixin],

        components: {},

        data() {
            return {
                isSending: false,
                isPasswordInvalid: false,
                isEmailInvalid: false,
                userRoleError: '',
                areInputsDisabledAfterFormIsSubmitted: false,
            }
        },

        computed: {
            invalidInputEmailClass() {
                return { 'form-input-text--is-invalid': this.isEmailInvalid }
            },

            invalidInputPasswordClass() {
                return { 'form-input-text--is-invalid': this.isPasswordInvalid }
            },

            practiceTypes() {
                return [
                    {
                        value: PracticeTypeEnum.DEMO_PHYSICIAN,
                        label: 'Specialist office',
                    },
                    {
                        value: PracticeTypeEnum.DEMO_ANCILLARY,
                        label: 'Ancillary service (Diagnostic, Therapeutic, Custodial )',
                    },
                    {
                        value: PracticeTypeEnum.DEMO_PRIMARY_CARE,
                        label: 'Primary care office',
                    },
                ]
            },
        },

        methods: {
            checkEmailOnBlur() {
                UsersAPI.checkRegistrationEmail({ email: this.data.email })
                    .then(() => {
                        this.isEmailInvalid = false
                        this.isSending = false
                        this.isSent = true
                        if (this.$validation.get('email')) {
                            this.$validation.clear()
                        }
                    })
                    .catch((error) => {
                        this.error = error.response
                        this.isEmailInvalid = true
                        this.isSending = false
                        this.triggerAutofocusInput()
                    })
            },

            checkPasswordOnBlur() {
                this.handleRequest(UsersAPI.checkRegistrationPassword({ password: this.data.password }))
                    .then(() => {
                        this.isPasswordInvalid = false
                        this.isSending = false
                        this.isSent = true
                        if (this.$validation.get('password')) {
                            this.$validation.clear()
                        }
                    })
                    .catch((error) => {
                        this.error = error.response
                        this.isPasswordInvalid = true
                        this.isSending = false
                        this.triggerAutofocusInput()
                    })
            },

            emailChange(event) {
                this.change(event)

                if (this.data.emailRepeat) {
                    this.checkEmailRepeatOnBlur()
                }
            },

            checkOnChange(event) {
                if (event.value.length >= 10) {
                    this.isPasswordInvalid = false
                    if (this.$validation.get('password')) {
                        this.$validation.clear()
                    }
                }
                this.change(event)
            },

            roleChanged(event) {
                this.userRoleError = ''
                this.change(event)
            },

            handleFormSubmission() {
                this.isSending = true
                const request = {
                    practiceType: this.data.practiceType,
                    email: this.data.email,
                    password: this.data.password,
                }
                UsersAPI.createDemo(request).then(() => {
                    AuthAPI.logIn(this.data.email, this.data.password)
                        .then((AuthenticationResult) => {
                            const { accessToken, idToken, refreshToken } = AuthenticationResult.signInUserSession
                            this.$auth.logIn(accessToken.jwtToken, idToken.jwtToken, refreshToken.token)
                            this.$router.push({ name: 'index.home' })
                            this.isSending = false
                        })
                        .catch(() => {
                            this.errorMessage = this.$t('forms.auth.login.microcopy.not_found_error')
                            this.data.password = ''
                            this.isSending = false
                        })
                })
            },
        },

        mounted() {},
    }
</script>
